import { useEffect, useState, useCallback } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, X, Check, Clock, FileText } from "lucide-react";

function ReviewModal({ submission, onClose, onDone }) {
  const [mode, setMode] = useState(null); // "approve" | "reject"
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "approve") {
        await api.post("/admin/kyc/approve", { submission_id: submission.id, notes });
        toast.success(`Approved ${submission.user_name || submission.user_email}`);
      } else {
        if (!reason.trim()) { toast.error("Reason is required"); setBusy(false); return; }
        await api.post("/admin/kyc/reject", { submission_id: submission.id, notes, reason });
        toast.success("Submission rejected — user notified");
      }
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const token = sessionStorage.getItem("anubandh_token");
  const docUrl = submission.storage_path ? `${API}/files/${submission.storage_path}?auth=${token}` : null;
  const isPdf = (submission.content_type || "").includes("pdf");

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-stone-200">
          <div>
            <div className="font-serif-display text-xl text-[#1E1B4B]">{submission.user_name || "—"}</div>
            <div className="text-xs text-stone-500">{submission.user_email} · {submission.id_type} · {submission.id_number_masked}</div>
          </div>
          <button data-testid="admin-close-modal-btn" onClick={onClose} className="p-2 rounded-full hover:bg-stone-100">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {docUrl && !isPdf && (
            <img src={docUrl} alt="KYC document" className="max-h-[400px] rounded-xl mx-auto border border-stone-200" data-testid="admin-kyc-image" />
          )}
          {docUrl && isPdf && (
            <a href={docUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-amber-700 underline">
              <FileText size={18} /> Open PDF document
            </a>
          )}
          {!docUrl && (
            <div className="text-sm text-stone-500 italic">Legacy submission — no photo/PDF attached.</div>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              data-testid="admin-mode-approve-btn"
              onClick={() => setMode("approve")}
              className={`rounded-xl p-3 text-sm border transition-colors ${mode === "approve" ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-stone-200 hover:border-emerald-300"}`}
            >
              <Check size={16} className="inline mr-1" /> Approve
            </button>
            <button
              data-testid="admin-mode-reject-btn"
              onClick={() => setMode("reject")}
              className={`rounded-xl p-3 text-sm border transition-colors ${mode === "reject" ? "border-red-500 bg-red-50 text-red-800" : "border-stone-200 hover:border-red-300"}`}
            >
              <X size={16} className="inline mr-1" /> Reject
            </button>
          </div>

          {mode && (
            <div className="mt-5 space-y-3">
              {mode === "reject" && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-stone-500">Reason (shown to user)</label>
                  <input
                    data-testid="admin-reject-reason-input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g., Document is blurry / does not match profile name"
                    className="mt-1 w-full border border-stone-300 rounded-xl px-4 py-2.5 focus:border-[#1E1B4B]"
                  />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-stone-500">Reviewer notes (internal)</label>
                <textarea
                  data-testid="admin-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optional — visible in audit trail"
                  className="mt-1 w-full border border-stone-300 rounded-xl px-4 py-2.5 focus:border-[#1E1B4B]"
                />
              </div>
              <button
                data-testid="admin-submit-review-btn"
                disabled={busy}
                onClick={submit}
                className={`btn-pill text-white px-6 py-2.5 text-sm disabled:opacity-40 ${mode === "approve" ? "" : ""}`}
                style={{ background: mode === "approve" ? "#059669" : "#B91C1C" }}
              >
                {busy ? "Working…" : mode === "approve" ? "Confirm approval" : "Confirm rejection"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminKYC() {
  const [subs, setSubs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kyc/pending");
      setSubs(data.submissions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center gap-3">
          <ShieldCheck className="text-amber-700" size={24} />
          <div>
            <div className="overline">Administrator</div>
            <h1 className="font-serif-display text-4xl text-[#1E1B4B]">KYC Review Queue</h1>
          </div>
        </div>
        <p className="mt-3 text-stone-600">Review each submission carefully. Approvals grant the trusted badge; rejections notify the user with your stated reason.</p>

        <div className="mt-10">
          {loading && <div className="text-stone-400 text-center py-12">Loading queue…</div>}
          {!loading && subs.length === 0 && (
            <div className="text-center py-16 border border-dashed border-stone-300 rounded-2xl">
              <ShieldCheck className="mx-auto text-stone-300" size={40} />
              <div className="mt-3 text-stone-500">No pending submissions.</div>
            </div>
          )}
          <div className="grid gap-4">
            {subs.map((s) => (
              <div key={s.id} data-testid={`admin-sub-${s.id}`} className="border border-stone-200 rounded-2xl bg-white p-5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center text-amber-800 font-serif-display text-lg shrink-0">
                  {(s.user_name || s.user_email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1E1B4B] truncate">{s.user_name || "—"}</div>
                  <div className="text-xs text-stone-500 truncate">{s.user_email}</div>
                  <div className="text-xs text-stone-600 mt-1">
                    {s.id_type} · {s.id_number_masked} · submitted {new Date(s.submitted_at).toLocaleString()}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-amber-50 text-amber-800 border border-amber-200">
                  <Clock size={12} /> Pending
                </span>
                <button
                  data-testid={`admin-review-btn-${s.id}`}
                  onClick={() => setSelected(s)}
                  className="btn-pill text-white text-sm px-5 py-2"
                  style={{ background: "#1E1B4B" }}
                >
                  Review
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <ReviewModal
          submission={selected}
          onClose={() => setSelected(null)}
          onDone={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
